import { useCallback, useState } from 'react'
import { useLogger } from 'hooks/logger'
import { useEndMutation, useStartMutation } from 'hooks/mutations'
import { useErrorDisplayer } from 'hooks/error'
// eslint-disable-next-line no-restricted-imports
import { useMutation, UseMutationConfig } from 'react-relay'
import { MutationParameters, GraphQLTaggedNode, ConcreteRequest, PayloadError } from 'relay-runtime'
import { trackError } from 'services/analytics'
import { graphQLErrorExtraData } from 'api/graphql/error'
import { GraphQLError } from 'types'
import { getErrorSeverity } from 'services/error'

export type IQuickCommitExecutorOptions<T extends MutationParameters> = Omit<
  UseMutationConfig<T>, 'variables' | 'onCompleted' | 'onError'
  > & {
  mutatingNodeId?: string
  throwErrors?: boolean
  trackErrors?: boolean
}

export interface IQuickCommitExecutor<T extends MutationParameters> {
  execute: (
    variables: T['variables'],
    options?: IQuickCommitExecutorOptions<T>
  ) => Promise<T['response'] | null>
  loading: boolean
  error?: Error | GraphQLError
}

/**
 * React hook to handle graphql mutations easily.
 * Useful to handle mutation variables and errors.
 *
 * TODO: there's a bunch that should be improved in this hook
 * - ability to pass variables in the hook call, instead of when calling execute, so that the execute call can
 *   be passed directly as a callback to children
 * - ability to pass options like the updater to the hook call itself instead of to the execute call
 * - updater function should be auto wrapped in try catch
 * - option to hide error display to the user, and to block error reporting to sentry which should be done by
 *   adding them as options to errorDisplayer then exposing its options in useQuickCommit options
 * - ability to specify multiple mutating node IDs, for example when approving a timesheet we should "mutate"
 *   the timesheet itself, not just the payroll item
 * - it should be mandatory to specify a callback that checks if the mutation was successful
 *   like on useMutationBasedFormSubmit
 * - ability to specify success message (like on useMutationBasedFormSubmit)
 *
 * @param mutation The mutation to be executed.
 */
export function useQuickCommit<T extends MutationParameters>(mutation: GraphQLTaggedNode): IQuickCommitExecutor<T> {
  const logger = useLogger('useQuickCommit')
  const errorDisplayer = useErrorDisplayer()
  const [commit, loading] = useMutation<T>(mutation)
  const [error, setError] = useState<GraphQLError | Error | undefined>(undefined)
  const startMutation = useStartMutation()
  const endMutation = useEndMutation()

  const execute = useCallback(async (
    variables: T['variables'],
    options?: IQuickCommitExecutorOptions<T>,
  ) => {
    const mutationExecution = startMutation({ nodeId: options?.mutatingNodeId })
    let result: T['response'] | null = null

    try {
      // TODO: discuss wrapping this function in startTransition() or at least provide it as the default option
      // so that react doesn't consider changes from the mutation as urgent
      result = await new Promise<T['response'] | null>((resolve, reject) => {
        logger.debug('commit', (mutation as ConcreteRequest)?.operation?.name)
        commit({
          ...options,
          variables,
          onCompleted: (_data, errors: PayloadError[] | null) => {
            logger.debug('commit onCompleted', (mutation as ConcreteRequest)?.operation?.name)
            const extraData = graphQLErrorExtraData({
              query: mutation.toString(),
              variables,
            })
            // If trackErrors is undefined, then the default should be true if the errors aren't thrown
            // If the errors are thrown, then don't track them, the consumer code will do it unless specified otherwise
            const track = options?.trackErrors ?? !options?.throwErrors
            if (errorDisplayer.displayMutationErrors((errors || []) as GraphQLError[], {
              trackingExtraData: extraData,
              track,
            })) {
              logger.debug('errorDisplayer has displayed error, running error handling code')
              // TODO: handle multiple errors?
              const newError = errors?.[0] as GraphQLError
              setError(newError)
              if (options?.throwErrors) {
                logger.debug('options.throwErrors is true, throwing')
                reject(newError)
              } else {
                logger.debug('options.throwErrors is false, resolving with null')
                resolve(null)
              }
            } else {
              logger.debug('errorDisplayer did not display any error, resolving with data')
              setError(undefined)
              resolve(_data)
            }
            endMutation(mutationExecution.mutationId)
          },
          onError: (_error) => {
            logger.debug('commit onError', (mutation as ConcreteRequest)?.operation?.name)
            const extraData = graphQLErrorExtraData({
              query: mutation.toString(),
              variables,
            })
            trackError(_error, getErrorSeverity(_error), extraData)
            errorDisplayer.displayError(_error)
            setError(_error)
            if (options?.throwErrors) {
              logger.debug('options.throwErrors is true, throwing')
              reject(_error)
            } else {
              logger.debug('options.throwErrors is false, resolving with null')
              resolve(null)
            }
            endMutation(mutationExecution.mutationId)
          },
        })
      })
    } catch (err) {
      trackError(err as Error)
      endMutation(mutationExecution.mutationId)

      if (options?.throwErrors) throw err
    }

    endMutation(mutationExecution.mutationId)

    return result
  }, [errorDisplayer, setError])

  return {
    loading,
    error,
    execute,
  }
}
